import React, { createContext, useContext, useState, useEffect } from 'react';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [awsCredentials, setAwsCredentials] = useState(null);
  const [error, setError] = useState(null);

  const cognitoConfig = {
    region: process.env.REACT_APP_AWS_REGION,
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
    identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
    domain: process.env.REACT_APP_COGNITO_DOMAIN,
    scope: ['openid', 'email', 'profile'],
    redirectSignIn: process.env.REACT_APP_APP_URL,
    redirectSignOut: process.env.REACT_APP_APP_URL,
    responseType: 'code'
  };

  const getHostedUIUrl = () => {
    const baseUrl = `https://${cognitoConfig.domain}.auth.${cognitoConfig.region}.amazoncognito.com`;
    const params = new URLSearchParams({
      client_id: cognitoConfig.userPoolWebClientId,
      response_type: 'code',
      scope: cognitoConfig.scope.join(' '),
      redirect_uri: cognitoConfig.redirectSignIn
    });
    return `${baseUrl}/oauth2/authorize?${params.toString()}`;
  };

  const getLogoutUrl = () => {
    const baseUrl = `https://${cognitoConfig.domain}.auth.${cognitoConfig.region}.amazoncognito.com`;
    const params = new URLSearchParams({
      client_id: cognitoConfig.userPoolWebClientId,
      logout_uri: cognitoConfig.redirectSignOut
    });
    return `${baseUrl}/logout?${params.toString()}`;
  };

  const login = () => {
    window.location.href = getHostedUIUrl();
  };

  const logout = () => {
    // Clear local state
    setIsAuthenticated(false);
    setUser(null);
    setAwsCredentials(null);
    
    // Clear localStorage
    localStorage.removeItem('cognitoTokens');
    
    // Redirect to Cognito logout
    window.location.href = getLogoutUrl();
  };

  const exchangeCodeForTokens = async (code) => {
    try {
      const tokenUrl = `https://${cognitoConfig.domain}.auth.${cognitoConfig.region}.amazoncognito.com/oauth2/token`;
      
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: cognitoConfig.userPoolWebClientId,
          code: code,
          redirect_uri: cognitoConfig.redirectSignIn
        })
      });

      if (!response.ok) {
        throw new Error('Token exchange failed');
      }

      const tokens = await response.json();
      
      // Store tokens
      localStorage.setItem('cognitoTokens', JSON.stringify(tokens));
      
      // Get user info from ID token
      const userInfo = parseJWTPayload(tokens.id_token);
      setUser(userInfo);
      setIsAuthenticated(true);

      // Set up AWS credentials
      await setupAWSCredentials(tokens);

      return tokens;
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
      throw error;
    }
  };

  const parseJWTPayload = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT:', error);
      return null;
    }
  };

  const setupAWSCredentials = async (tokens) => {
    try {
      const credentials = fromCognitoIdentityPool({
        clientConfig: { region: cognitoConfig.region },
        identityPoolId: cognitoConfig.identityPoolId,
        logins: {
          [`cognito-idp.${cognitoConfig.region}.amazonaws.com/${cognitoConfig.userPoolId}`]: tokens.id_token
        }
      });

      setAwsCredentials(credentials);
      console.log('AWS credentials configured successfully');
    } catch (error) {
      console.error('Error setting up AWS credentials:', error);
    }
  };

  const refreshTokens = async () => {
    try {
      const storedTokens = localStorage.getItem('cognitoTokens');
      if (!storedTokens) {
        throw new Error('No tokens found');
      }

      const tokens = JSON.parse(storedTokens);
      if (!tokens.refresh_token) {
        throw new Error('No refresh token found');
      }

      const tokenUrl = `https://${cognitoConfig.domain}.auth.${cognitoConfig.region}.amazoncognito.com/oauth2/token`;
      
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: cognitoConfig.userPoolWebClientId,
          refresh_token: tokens.refresh_token
        })
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const newTokens = await response.json();
      
      // Update stored tokens
      const updatedTokens = { ...tokens, ...newTokens };
      localStorage.setItem('cognitoTokens', JSON.stringify(updatedTokens));
      
      // Update AWS credentials
      await setupAWSCredentials(updatedTokens);

      return updatedTokens;
    } catch (error) {
      console.error('Error refreshing tokens:', error);
      logout();
      throw error;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check for authorization code in URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        
        if (code) {
          // Exchange code for tokens
          await exchangeCodeForTokens(code);
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          // Check for existing tokens
          const storedTokens = localStorage.getItem('cognitoTokens');
          if (storedTokens) {
            const tokens = JSON.parse(storedTokens);
            
            // Check if tokens are expired
            const userInfo = parseJWTPayload(tokens.id_token);
            const currentTime = Math.floor(Date.now() / 1000);
            
            if (userInfo && userInfo.exp > currentTime) {
              setUser(userInfo);
              setIsAuthenticated(true);
              await setupAWSCredentials(tokens);
            } else {
              // Try to refresh tokens
              try {
                await refreshTokens();
              } catch (error) {
                console.log('Token refresh failed, user needs to login again');
              }
            }
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const value = {
    isAuthenticated,
    user,
    loading,
    awsCredentials,
    error,
    login,
    logout,
    refreshTokens
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};